/**
 * weaponPickupStep.ts
 * Per-tick weapon pickup detection for Starfall Courier.
 *
 * Checks Nova's hitbox against uncollected weapon pickups. On overlap the
 * pickup is consumed: `gameState.projectileKind` is swapped to the pickup's
 * variant and `gameState.ammo` is seeded from the per-kind table below.
 *
 * Also exports {@link resetWeaponOnHit} for the damage system to call when
 * Nova takes a hit — reverts the active weapon back to the default
 * `'star_shard'` and clears ammo. The Shield power-up guard lives at the
 * call site (see TODO in {@link resetWeaponOnHit}) because Shield state
 * has not landed yet; until it does, every hit drops the weapon.
 *
 * No React imports. No async calls. Pure engine logic.
 *
 * @module weaponPickupStep
 */

import { gameState, type WeaponPickupRuntime, type WeaponPickupKind } from './gameState';
import { NOVA_WIDTH, NOVA_HEIGHT, TILE_SIZE } from '../../constants/gameConstants';
import { dispatch } from './gameEventDispatch';

/** Hitbox size for weapon pickups (same as tile size, like other collectibles). */
const WEAPON_PICKUP_SIZE = TILE_SIZE;

/**
 * Default ammo granted per weapon kind on pickup. Keep these in one place
 * so the HUD and any future balancing tool read the same source of truth.
 *
 * Greybox values \u2014 tuning happens once the full weapon system ships.
 * - Gem Shard: 10 (per ROADMAP "fast, 10 shots")
 * - Boomerang: 8 (lower because each shot has 3 s travel time)
 * - Fire Orb: 6 (lower because AoE is high-impact)
 * - Ice Crystal: 8 (mid \u2014 freeze is utility, not raw DPS)
 */
export const WEAPON_AMMO: Record<WeaponPickupKind, number> = {
  gem_shard: 10,
  boomerang: 8,
  fire_orb: 6,
  ice_crystal: 8,
};

/**
 * Check Nova against all uncollected weapon pickups this tick.
 * Mutates `gameState.weaponPickups` (sets `collected=true`),
 * `gameState.projectileKind`, and `gameState.ammo`.
 */
export function stepWeaponPickups(): void {
  const { nova, weaponPickups } = gameState;
  const novaRight = nova.x + NOVA_WIDTH;
  const novaBottom = nova.y + NOVA_HEIGHT;

  for (let i = 0; i < weaponPickups.length; i++) {
    const p: WeaponPickupRuntime = weaponPickups[i];
    if (p.collected) continue;

    // AABB overlap (inline for the hot path \u2014 same pattern as collectibleStep).
    const pRight = p.x + WEAPON_PICKUP_SIZE;
    const pBottom = p.y + WEAPON_PICKUP_SIZE;
    if (nova.x >= pRight || novaRight <= p.x || nova.y >= pBottom || novaBottom <= p.y) {
      continue;
    }

    // DECISION: Marking collected inline (no splice) keeps O(1) per pickup
    // and matches stepCollectibles' contract \u2014 the renderer/loader skips
    // collected entries; physics ignores them.
    p.collected = true;
    gameState.projectileKind = p.kind;
    gameState.ammo = WEAPON_AMMO[p.kind];
    dispatch.weaponPickupCollected();
  }
}

/**
 * Revert Nova's active weapon back to the default `'star_shard'` with zero
 * ammo. Intended to be called from the damage system when Nova takes a hit.
 *
 * The caller is responsible for the Shield guard \u2014 see module JSDoc.
 * TODO(shield-powerup): once `nova.shieldActive` (or equivalent) exists,
 * the damage system should skip this call while the shield is up.
 */
export function resetWeaponOnHit(): void {
  gameState.projectileKind = 'star_shard';
  gameState.ammo = 0;
}
